import { Link, createFileRoute } from '@tanstack/react-router';

import { PageTitle } from '@components/PageTitle';

export const Route = createFileRoute('/_layout/(private)/home')({
  component: Home,
});

function Home() {
  return (
    <div className="w-full space-y-3 overflow-y-auto">
      <PageTitle title="Content Management System (CMS)" />
      <p>
        Seja bem-vindo(a) ao <strong>CMS GPMED</strong>. Sua expertise é
        fundamental para garantir a segurança e a precisão técnica dos conteúdos
        que auxiliam milhares de colegas médicos na tomada de decisão clínica.
      </p>
      <p className="mb-8">
        Abaixo, apresentamos as diretrizes metodológicas essenciais para o
        processo de revisão e estruturação de conteúdos em nossa plataforma.
      </p>

      <hr className="mb-8" />

      <p>
        <strong>1. Filosofia e Rigor Técnico</strong>
        <br />A curadoria do GPMED preza pela excelência científica e pela
        aplicabilidade prática no cotidiano médico.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Fundamentação Científica:</strong> Todo o conteúdo deve
        ser obrigatoriamente embasado em referências científicas atualizadas.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Foco em Decisão Clínica:</strong> O objetivo não é
        realizar uma revisão sistemática extensa, mas sim consolidar um guia
        prático com informações cruciais para a tomada de decisão.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Ênfase em Condutas:</strong> Como a plataforma é
        voltada ao suporte de manejo, a seção de tratamento e condutas deve ser
        detalhada, completa e minuciosa.
      </p>
      <p className="mb-8 ml-6">
        &#x2022; <strong>Integridade Acadêmica:</strong> É estritamente proibida
        e inadmissível a prática de plágio.
      </p>

      <hr className="mb-8" />

      <p>
        <strong>2. Estrutura e Organização do Conteúdo</strong>
        <br />A organização didática e a formatação são os pilares da clareza do
        nosso sistema.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Organização por Tópicos:</strong> Cada protocolo é
        dividido em tópicos estruturados. Você possui autonomia para reordenar,
        alterar ou adicionar tópicos, desde que essa mudança favoreça a didática
        ou a completude do protocolo.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Uso de Indentação:</strong> Para otimizar a
        legibilidade e possibilitar uma “consulta rápida” no plantão, os textos
        devem ser estruturados em itens e subitens, evitando-se o uso de
        parágrafos de texto corrido extensos.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Contextualização Inicial:</strong> Os primeiros tópicos
        devem abordar informações relevantes como diagnóstico, epidemiologia,
        quadro clínico e classificações.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Objetividade:</strong> Devem ser omitidas informações
        puramente teóricas ("rodapés de livro") ou que não possuam utilidade
        direta na prática clínica. Por outro lado, toda informação relevante
        para a decisão clínica, especialmente no contexto de Atenção Primária à
        Saúde, deve ser inserida.
      </p>
      <p className="mb-8 ml-6">
        &#x2022; <strong>Manejo e Tópicos Extensos:</strong> Os tópicos
        subsequentes devem abranger todo o manejo e conduta. Caso o manejo seja
        extenso, é necessário subdividi-lo em diferentes tópicos para manter a
        fluidez da leitura.
      </p>

      <hr className="mb-8" />

      <p>
        <strong>3. Padrão de Escrita e Didática</strong>
        <br />O texto deve ser técnico e preciso, porém acessível e didático o
        suficiente para que estudantes de medicina pré-internato também
        compreendam exatamente a conduta a ser adotada
      </p>
      <p className="ml-6">
        &#x2022; <strong>Revisão Minuciosa:</strong> É essencial ler e reler o
        conteúdo para corrigir erros gramaticais, ajustar termos técnicos e
        reescrever sentenças que prejudiquem a clareza, bem como garantir a
        acurácia técnica da informação.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Ajuste de Elementos:</strong> Avalie constantemente a
        ordem do conteúdo e ajuste itens e subitens para otimizar a experiência
        do usuário.
      </p>
      <p className="mb-8 ml-6">
        &#x2022; <strong>Precisão Farmacológica:</strong> Deve-se conferir
        detalhadamente as doses e posologias de cada medicamento mencionado.
      </p>

      <hr className="mb-8" />

      <p>
        <strong>4. Padronização de Medicamentos</strong>
        <br />
        Para garantir a segurança na prescrição, os medicamentos devem seguir
        rigorosamente o seguinte formato de exibição:
      </p>

      <div className="prose marker:text-foreground text-foreground prose-li:my-0 prose-ul:ml-6 prose-ul:mt-0 prose-ul:mb-5 [&_ul_ul]:list-[circle]">
        <ul>
          <li>
            <strong>[Nome do medicamento]</strong> [apresentação]
          </li>
          <ul>
            <li>[Posologia descrita de um modo didático].</li>
          </ul>
        </ul>
      </div>

      <p>Exemplos:</p>
      <div className="prose marker:text-foreground text-foreground prose-li:my-0 prose-ul:ml-6 prose-ul:mt-0 prose-ul:mb-5 max-w-full [&_ul_ul]:list-[circle]">
        <ul>
          <li>
            <strong>Dipirona</strong> comp. 500mg
          </li>
          <ul>
            <li>Tomar 01 cp de 6/6h, se dor ou febre (máx. 4g/dia).</li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Loratadina</strong> xp. 1mg/1mL
          </li>
          <ul>
            <li>≥ 30 kg: Tomar 10 mL uma vez ao dia (max 10 mg/dia).</li>
            <li>&lt; 30 kg: Tomar 5 mL uma vez ao dia (max 5 mg/dia).</li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Diclofenaco</strong> sol. inj. 75mg/3mL (intramuscular)
          </li>
          <ul>
            <li>Aplicar 1 amp (75mg), IM (max 75mg por dia).</li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Cetorolaco</strong> sol. inj. 30mg/1mL (intravenoso)
          </li>
          <ul>
            <li>
              &lt; 65 anos: 1 ampola (30mg) IV bolus lento (max 90mg por dia).
            </li>
            <li>
              &gt; 65 anos: 1/2 amp (15mg) IV bolus lento (max 60mg por dia).
            </li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Ceftriaxona</strong> pó inj. 500mg ou 1g
          </li>
          <ul>
            <li>
              Aplicar 2 amp (2g) + 40ml SF0,9%, IV em 30 min de 24/24h (não usar
              Ringer)
            </li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Sinvastatina</strong> comp. 10mg ou 20mg ou 40mg
          </li>
          <ul>
            <li>Tomar 1 cp (de 20 ou 40mg) à noite (máx. 40 mg/dia).</li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Enalapril</strong> comp. 5mg ou 10mg ou 20mg
          </li>
          <ul>
            <li>Dose diária: 5 a 40 mg</li>
            <li>Frequência: 1 a 2 vezes ao dia.</li>
          </ul>
        </ul>

        <ul>
          <li>
            <strong>Metformina</strong> (Glifage®) comp. 500mg ou 850mg ou
            1.000mg
          </li>
          <ul>
            <li>
              Dose: 500 a 2.550 mg/dia (adultos) e 500 a 2.000 mg/dia
              (crianças).
            </li>
            <li>Frequência: 2 a 3 vezes ao dia.</li>
          </ul>
        </ul>
      </div>

      <p>Observações:</p>
      <p className="ml-6">
        &#x2022; O nome do medicamento deve vir em negrito.
      </p>
      <p className="ml-6">
        &#x2022; A descrição deve ser enxuta, assemelhando-se a uma prescrição
        real, quando possível.
      </p>
      <p className="mb-8 ml-6">
        &#x2022; Sempre que possível, <strong>inclua a dose máxima</strong>{' '}
        permitida entre parênteses ao final: (máx. X mg/dia).
      </p>

      <hr className="mb-8" />

      <p>
        <strong>5. Fontes e Referências</strong>
        <br />O último tópico de cada protocolo será sempre dedicado às
        referências utilizadas.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Qualidade das Fontes:</strong> Utilize apenas fontes
        robustas. É proibido o uso de sites leigos, blogs ou cópia de conteúdos
        protegidos por direitos autorais. Sempre que possível, cite as fontes
        primárias da informação.
      </p>
      <p className="ml-6">
        &#x2022; <strong>Formatação de Citação:</strong> As referências devem
        ser enumeradas no formato [1], [2], [3] e citadas ao longo do corpo do
        texto nos locais correspondentes.
      </p>
      <p className="mb-8 ml-6">
        &#x2022; <strong>Dados Obrigatórios:</strong> Cada referência deve
        conter, no mínimo: Autor, Título, Ano e Link.
      </p>

      <hr className="mb-8" />

      <p>
        <strong>6. Suporte e Comunicação</strong>
        <br />
        Caso surjam dúvidas técnicas durante o processo de edição que possam
        impactar na qualidade do protocolo, utilize a função{' '}
        <strong>"Adicionar observação"</strong> e envie o conteúdo para{' '}
        <strong>aprovação</strong>. Nossa equipe analisará e devolverá o
        protocolo com os devidos esclarecimentos nas observações.
      </p>

      <p className="mb-8">
        Para dúvidas adicionais, entre em contato através dos canais:
        <br />
        WhatsApp: (17) 99781-9214
        <br />
        E-mail: contato@oguiadeprescricao.com.br
      </p>

      <hr className="mb-8" />

      <p>
        <strong>Vamos começar?</strong>
      </p>
      <p>
        Na aba à esquerda da tela você encontrará o botão “Revisar” e o botão
        “Criar”. Acesse-os que você verá os protocolos disponíveis para revisão
        ou criação. Basta escolher o protocolo e clicar em “Revisar protocolo”
        ou “Criar protocolo”.
      </p>
      <p>
        Qualquer dificuldade no uso do sistema, entre em contato pelo nosso
        suporte citado acima.
      </p>
      <p className="text-center">
        Ao utilizar o Content Management System (CMS), você concorda com os{' '}
        <Link
          className="text-tertiary font-semibold hover:underline hover:decoration-1"
          target="_blank"
          to="/terms-and-conditions">
          termos de uso
        </Link>{' '}
        e{' '}
        <Link
          className="text-tertiary font-semibold hover:underline hover:decoration-1"
          target="_blank"
          to="/privacy-policy">
          política de privacidade
        </Link>{' '}
        do GPMED.
      </p>
    </div>
  );
}
